/**
 * @fileoverview Timeline visualization component for audit log entries.
 * @module ui-admin/components/AuditLogTimeline
 *
 * Responsibilities:
 * - Render a vertical timeline of audit entries with expandable details
 * - Handle paginated loading via fetchFn
 * - Filter entries by action type
 * - Open AuditLogDetailPanel on entry click
 *
 * Dependencies:
 * - @firesquirrel/audit (AuditLogEntry, AuditAction, ChangeDetail types, formatAuditFieldLabel)
 * - ./AuditLogDetailPanel (detail modal)
 * - ../utils/formatAuditValue (human-readable value formatting)
 * - ../contexts/ThemeContext (GridTheme)
 *
 * Architecture:
 * - Presentational component with internal fetch state.
 * - Data comes via fetchFn prop (backed by Dexie reads in the parent).
 * - Listens to 'fs:audit-log-written' CustomEvent to auto-refresh.
 *
 * Notes:
 * - Compound action filters (e.g. 'addMember,removeMember') are applied client-side
 * - Inline changes shown for 1-2 fields; expandable section for 3+
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Activity, ScrollText, ChevronDown, ChevronUp, Monitor, Smartphone, Tablet, Server } from 'lucide-react';
import type { AuditLogEntry, AuditAction, ChangeDetail } from '@firesquirrel/audit';
import type { GridTheme } from '../contexts/ThemeContext';
import AuditLogDetailPanel from './AuditLogDetailPanel';
import { formatAuditValue } from '../utils/formatAuditValue';
import { expandAuditChanges } from '../utils/expandAuditChanges';
import { formatAuditFieldLabel, shouldHideAuditField } from '../../../audit/src/summaries';

export type { AuditAction, ChangeDetail, AuditLogEntry };

interface AuditLogWrittenDetail {
  collection?: string;
  documentId?: string;
  entryId?: string;
}

// ---------------------------------------------------------------------------
// Action color map — matches LocationsPage ACTIVITY_ICON_MAP pattern
// ---------------------------------------------------------------------------

const ACTION_DOT_COLORS: Record<string, string> = {
  create: 'bg-green-500',
  update: 'bg-blue-500',
  delete: 'bg-red-500',
  restore: 'bg-emerald-500',
  statusChange: 'bg-yellow-500',
  addMember: 'bg-teal-500',
  removeMember: 'bg-pink-500',
  publish: 'bg-purple-500',
  archive: 'bg-gray-400',
  clone: 'bg-indigo-500',
  seed: 'bg-amber-500',
  provision: 'bg-green-600',
  assign: 'bg-cyan-500',
  unassign: 'bg-orange-400',
  enable: 'bg-green-400',
  disable: 'bg-gray-500',
  bulk: 'bg-blue-400',
  trialExtend: 'bg-amber-400',
  trialConvert: 'bg-green-500',
  trialCleanup: 'bg-gray-400',
  trialExpire: 'bg-red-500',
  inviteSent: 'bg-teal-500',
  inviteResent: 'bg-teal-400',
  inviteRevoked: 'bg-pink-500',
  inviteAccepted: 'bg-green-500',
  inviteTokenValidated: 'bg-teal-300',
  loginSuccess: 'bg-blue-400',
  loginDenied: 'bg-red-500',
  signupBlocked: 'bg-red-400',
  snapshotUpgrade: 'bg-amber-500',
};

const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  restore: 'Restored',
  statusChange: 'Status Changed',
  addMember: 'Member Added',
  removeMember: 'Member Removed',
  publish: 'Published',
  archive: 'Archived',
  clone: 'Cloned',
  seed: 'System Seeded',
  provision: 'Provisioned',
  assign: 'Assigned',
  unassign: 'Unassigned',
  enable: 'Enabled',
  disable: 'Disabled',
  bulk: 'Bulk Operation',
  trialExtend: 'Trial Extended',
  trialConvert: 'Converted to Active',
  trialCleanup: 'Trial Cleaned Up',
  trialExpire: 'Trial Expired',
  inviteSent: 'Invite Sent',
  inviteResent: 'Invite Resent',
  inviteRevoked: 'Invite Revoked',
  inviteAccepted: 'Invite Accepted',
  inviteTokenValidated: 'Invite Validated',
  loginSuccess: 'Logged In',
  loginDenied: 'Login Denied',
  signupBlocked: 'Signup Blocked',
  snapshotUpgrade: 'Checklist Updated',
};

const DEVICE_ICONS: Record<string, typeof Monitor> = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
  system: Server,
};

const FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: 'All Actions', value: '' },
  { label: 'Created', value: 'create' },
  { label: 'Updated', value: 'update' },
  { label: 'Deleted', value: 'delete' },
  { label: 'Status Changed', value: 'statusChange' },
  { label: 'Members', value: 'addMember,removeMember' },
  { label: 'Published', value: 'publish' },
  { label: 'Archived', value: 'archive' },
  { label: 'Provisioned', value: 'provision' },
  { label: 'Trial Events', value: 'trialExtend,trialConvert,trialExpire,trialCleanup' },
  { label: 'Invitations', value: 'inviteSent,inviteResent,inviteRevoked,inviteAccepted' },
  { label: 'Login Events', value: 'loginSuccess,loginDenied,signupBlocked' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * @contract AuditLogTimelineProps
 *
 * Purpose: Props for the AuditLogTimeline component.
 * Fields: documentId, collection, theme, active, fetchFn, className.
 * Rules: fetchFn must return paginated { data, total, limit, skip } shape.
 * Sync: N/A — prop interface.
 */
export interface AuditLogTimelineProps {
  /** Document ID to fetch audit logs for */
  documentId: string;
  /** MongoDB collection name */
  collection: string;
  /** Grid theme */
  theme: GridTheme;
  /** Set true to begin fetching (enables lazy load on tab activation) */
  active?: boolean;
  /** Data fetcher — returns { data, total }, may be async */
  fetchFn: (collection: string, documentId: string, options?: { limit?: number; skip?: number; action?: string }) => Promise<{
    data: AuditLogEntry[];
    total: number;
    limit: number;
    skip: number;
  }> | {
    data: AuditLogEntry[];
    total: number;
    limit: number;
    skip: number;
  };
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component AuditLogTimeline
 *
 * Purpose: Renders a vertical timeline of audit entries with expandable details.
 * Props: documentId, collection, theme, active, fetchFn, className.
 * Behavior: Fetches on activation, auto-refreshes on audit write events,
 *   supports action filtering and paginated "Load More".
 * Data Source: local (entries fetched via fetchFn backed by Dexie)
 * Constraints: No direct API calls. fetchFn is the data boundary.
 * Performance: Renders up to PAGE_SIZE entries per load; "Load More" for pagination.
 */
export default function AuditLogTimeline({
  documentId,
  collection,
  theme: t,
  active = true,
  fetchFn,
  className = '',
}: AuditLogTimelineProps) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [actionFilter, setActionFilter] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  const PAGE_SIZE = 20;

  // Reset when document changes
  useEffect(() => {
    setEntries([]);
    setTotal(0);
    setLoaded(false);
    setExpandedIds(new Set());
  }, [documentId, collection]);

  // Fetch on activation
  const loadEntries = useCallback(
    async (skip = 0, append = false) => {
      setLoading(true);
      try {
        const actionParam = actionFilter.includes(',') ? undefined : actionFilter || undefined;
        const result = await fetchFn(collection, documentId, {
          limit: PAGE_SIZE,
          skip,
          action: actionParam,
        });

        let data = result.data;
        // Client-side filter for compound filters like "addMember,removeMember"
        if (actionFilter.includes(',')) {
          const actions = actionFilter.split(',');
          data = data.filter((e) => actions.includes(e.action));
        }

        setEntries((prev) => (append ? [...prev, ...data] : data));
        setTotal(result.total);
        setLoaded(true);
      } catch {
        setEntries([]);
        setTotal(0);
        setLoaded(true);
      } finally {
        setLoading(false);
      }
    },
    [fetchFn, collection, documentId, actionFilter],
  );

  useEffect(() => {
    if (active && !loaded) {
      loadEntries(0);
    }
  }, [active, loaded, loadEntries]);

  useEffect(() => {
    if (!active || typeof window === 'undefined') return;

    const handleAuditWrite = (event: Event) => {
      const detail = (event as CustomEvent<AuditLogWrittenDetail>).detail;
      if (!detail) return;
      if (detail.collection !== collection || detail.documentId !== documentId) return;
      loadEntries(0);
    };

    window.addEventListener('fs:audit-log-written', handleAuditWrite as EventListener);
    return () => window.removeEventListener('fs:audit-log-written', handleAuditWrite as EventListener);
  }, [active, collection, documentId, loadEntries]);

  // Re-fetch when filter changes
  useEffect(() => {
    if (loaded) {
      loadEntries(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionFilter]);

  const hasMore = useMemo(() => entries.length < total, [entries.length, total]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Loading state
  if (!loaded && loading) {
    return (
      <div className={`space-y-3 p-4 ${className}`}>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="h-3 w-3 rounded-full bg-gray-200 mt-1 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-gray-200 rounded-sm w-1/3" />
              <div className="h-3 bg-gray-200 rounded-sm w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Header + filter */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500 uppercase">Audit Log</span>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="text-[10px] font-medium text-gray-600 border border-gray-200 rounded-sm px-1.5 py-0.5 bg-white"
        >
          {FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Empty state */}
      {loaded && entries.length === 0 && (
        <div className="text-center py-6">
          <ScrollText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-600">No audit history recorded</p>
          <p className="text-xs text-gray-400 mt-1">Changes to this record will appear here</p>
        </div>
      )}

      {/* Timeline */}
      {entries.length > 0 && (
        <div className="relative pl-4">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-gray-200" />

          {entries.map((entry) => {
            const dotColor = ACTION_DOT_COLORS[entry.action] || 'bg-gray-400';
            const label = ACTION_LABELS[entry.action] || entry.action;
            const DeviceIcon = DEVICE_ICONS[entry.source.deviceType] || Monitor;
            const displayChanges = expandAuditChanges(entry.changes).filter((ch) => !shouldHideAuditField(ch.field));
            const hasChanges = displayChanges.length > 0;
            const isExpanded = expandedIds.has(entry._id);
            const date = new Date(entry.createdAt);

            return (
              <div key={entry._id} className="flex gap-2">
                {/* Timeline dot */}
                <div className="shrink-0 mt-1">
                  <div className={`h-2.5 w-2.5 rounded-full ${dotColor}`} />
                </div>

                {/* Entry content - clickable to show detail panel */}
                <div
                  className="flex-1 pb-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-sm transition-colors"
                  onClick={() => setSelectedEntry(entry)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      setSelectedEntry(entry);
                    } else if (e.key === ' ') {
                      e.preventDefault();
                      setSelectedEntry(entry);
                    }
                  }}
                >
                  {/* Timestamp + action badge */}
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] text-gray-400">
                      {new Date(entry.createdAt).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                    <span
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                        entry.action === 'create'
                          ? 'bg-green-100 text-green-700'
                          : entry.action === 'update'
                            ? 'bg-blue-100 text-blue-700'
                            : entry.action === 'delete'
                              ? 'bg-red-100 text-red-700'
                              : entry.action === 'statusChange'
                                ? 'bg-yellow-100 text-yellow-700'
                                : entry.action === 'addMember'
                                  ? 'bg-teal-100 text-teal-700'
                                  : entry.action === 'removeMember'
                                    ? 'bg-pink-100 text-pink-700'
                                    : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {label}
                    </span>
                  </div>

                  {/* User + source */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-700">{entry.performedBy.name}</span>
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <DeviceIcon className="h-2.5 w-2.5" />
                      {entry.source.app}
                    </span>
                  </div>

                  {/* Summary */}
                  <div className="text-xs text-gray-600">{entry.summary}</div>

                  {/* Reason (if present) */}
                  {entry.metadata?.reason && (
                    <div className="text-[11px] text-gray-500 italic mt-0.5">
                      Reason: {entry.metadata.reason}
                    </div>
                  )}

                  {/* Inline changes (1-2 changes shown directly) */}
                  {hasChanges && displayChanges.length <= 2 && (
                    <div className="mt-0.5">
                      {displayChanges.map((ch, ci) => (
                        <div key={ci} className="text-[11px] text-gray-500">
                          <span className="font-medium text-gray-600">{formatAuditFieldLabel(ch.field)}</span>
                          {': '}
                          <span className="line-through text-red-400">{formatAuditValue(ch.oldValue)}</span>
                          {' \u2192 '}
                          <span className="text-green-600 font-medium">{formatAuditValue(ch.newValue)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Expandable changes (3+ changes) */}
                  {hasChanges && displayChanges.length > 2 && (
                    <div className="mt-0.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(entry._id);
                        }}
                        className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {displayChanges.length} field changes
                      </button>
                      {isExpanded && (
                        <div className="mt-1 pl-2 border-l-2 border-gray-200 space-y-0.5">
                          {displayChanges.map((ch, ci) => (
                            <div key={ci} className="text-[11px] text-gray-500">
                              <span className="font-medium text-gray-600">{formatAuditFieldLabel(ch.field)}</span>
                              {': '}
                              <span className="line-through text-red-400">{formatAuditValue(ch.oldValue)}</span>
                              {' \u2192 '}
                              <span className="text-green-600 font-medium">{formatAuditValue(ch.newValue)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Click hint */}
                  <div className="text-[10px] text-gray-400 mt-1">Click for full details</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="text-center pt-2">
          <button
            onClick={() => loadEntries(entries.length, true)}
            disabled={loading}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400 px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            {loading ? 'Loading...' : `Load More (${entries.length} of ${total})`}
          </button>
        </div>
      )}

      {/* Detail Panel Modal */}
      {selectedEntry && (
        <AuditLogDetailPanel
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Legacy alias — kept for any external consumers; new code should import
// formatAuditValue from '../utils/formatAuditValue' directly.
const formatValue = formatAuditValue;
